'use client';

import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { priceInfo as getPriceInfo } from '@/utils/price';
import { getCurrentScrollPercentage, getViewArea } from '@/utils/scroll';
import { getEntrancePageScene } from '@/utils/tracking/entrancePageScene';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { BannerData, MarketingRuntimeConfig } from '../types';
import {
  buildCampaignButtonClickProperties,
  buildCampaignCollectButtonClickProperties,
  buildCampaignCouponMktClickProperties,
  buildCampaignCouponMktExposureProperties,
  buildCampaignProductTrackingProperties,
  buildCampaignTopBannerAdClickEvent,
  buildCampaignTopBannerAdExposureEvent,
  getCampaignTrackingCommonProperties,
} from './campaignTrackingProperties';

export function useCampaignPageTracking(runtimeConfig: MarketingRuntimeConfig) {
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);

  const enterTimer = useRef<number>(Date.now());
  const lastScrollState = useRef({ viewAdvance: 0, viewArea: 1 });
  const routerInfoRef = useRef(getFromRouterInfo());
  // 与种草弹窗曝光等事件保持一致：优先使用埋点字段 page_name
  const commonCampaignProperties = useMemo(
    () => getCampaignTrackingCommonProperties(runtimeConfig),
    [runtimeConfig],
  );

  const withCampaignProperties = useCallback(
    (properties: Record<string, any>) => ({
      ...commonCampaignProperties,
      ...properties,
    }),
    [commonCampaignProperties],
  );

  const trackPageView = useCallback(() => {
    const { refer_page_name, refer_url_path } = routerInfoRef.current;
    enterTimer.current = Date.now();

    try {
      trackEvent(
        {
          eventName: 'CampaignPage_View',
          properties: withCampaignProperties({
            refer_page_name,
            refer_url_path,
            entrance_page_scene: getEntrancePageScene({
              referPageName: refer_page_name,
              referUrlPath: refer_url_path,
            }),
          }),
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('Campaign page view tracking error:', error);
    }
  }, [trackEvent, withCampaignProperties]);

  const trackPageLeave = useCallback(() => {
    const { viewAdvance, viewArea } = lastScrollState.current;

    try {
      trackEvent(
        {
          eventName: 'CampaignPage_Leave',
          properties: withCampaignProperties({
            view_advance: viewAdvance,
            view_duration: Math.ceil((Date.now() - enterTimer.current) / 1000),
            view_area: viewArea,
          }),
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('Campaign page leave tracking error:', error);
    }
  }, [trackEvent, withCampaignProperties]);

  const trackNavTabClick = useCallback(
    (tabName: string, index: number, moduleName = '导航位') => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageNavTab_Click',
            properties: withCampaignProperties({
              module_name: moduleName,
              pits_index: index,
              tab_name: tabName,
            }),
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Campaign page nav tab click tracking error:', error);
      }
    },
    [trackEvent, withCampaignProperties],
  );

  const trackViewMoreClick = useCallback(
    (tabName?: string, moduleName = '导航位') => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageViewMoreBtn_Click',
            properties: withCampaignProperties({
              ...(tabName ? { tab_name: tabName } : {}),
              module_name: moduleName,
            }),
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Campaign page view more click tracking error:', error);
      }
    },
    [trackEvent, withCampaignProperties],
  );

  const trackProductListPitsClick = useCallback(
    (item: any, index: number, moduleName: string, tabName?: string) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        const properties: Record<string, any> = {
          module_name: moduleName,
          pits_index: index,
          ...buildCampaignProductTrackingProperties(item, {
            productPrice: parseFloat(nowPriceInfo.price || '0'),
            currentProductPrice: parseFloat(nowPriceInfo.sell_price || '0'),
            baseCurrentProductPrice: parseFloat(basePriceInfo.sell_price || '0'),
            dollarExchangeRate: parseFloat(currency?.exchange_rate || '0'),
            currencyName: currency?.name || '',
          }),
        };

        if (tabName) {
          properties.tab_name = tabName;
        }

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Click',
            properties: withCampaignProperties(properties),
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Campaign page product click tracking error:', error);
      }
    },
    [currency, trackEvent, userInfo, withCampaignProperties],
  );

  const trackProductListPitsExposure = useCallback(
    (item: any, index: number, moduleName: string, tabName?: string) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        const properties: Record<string, any> = {
          module_name: moduleName,
          pits_index: index,
          ...buildCampaignProductTrackingProperties(item, {
            productPrice: parseFloat(nowPriceInfo.price || '0'),
            currentProductPrice: parseFloat(nowPriceInfo.sell_price || '0'),
            baseCurrentProductPrice: parseFloat(basePriceInfo.sell_price || '0'),
            dollarExchangeRate: parseFloat(currency?.exchange_rate || '0'),
            currencyName: currency?.name || '',
          }),
        };

        if (tabName) {
          properties.tab_name = tabName;
        }

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Exp',
            properties: withCampaignProperties(properties),
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Campaign page product exposure tracking error:', error);
      }
    },
    [currency, trackEvent, userInfo, withCampaignProperties],
  );

  const trackAdExp = useCallback(
    (moduleName: string, tabName: string, index: number, extraProperties?: Record<string, any>) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageAD_Exp',
            properties: withCampaignProperties({
              module_name: moduleName,
              tab_name: tabName,
              pits_index: index,
              ...extraProperties,
            }),
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Campaign page ad exp tracking error:', error);
      }
    },
    [trackEvent, withCampaignProperties],
  );

  const trackAdClick = useCallback(
    (
      moduleName: string,
      index: number,
      tabName?: string,
      extraProperties?: Record<string, any>,
    ) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageAD_Click',
            properties: withCampaignProperties({
              module_name: moduleName,
              pits_index: index,
              ...(tabName ? { tab_name: tabName } : {}),
              ...extraProperties,
            }),
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Campaign page ad click tracking error:', error);
      }
    },
    [trackEvent, withCampaignProperties],
  );

  const trackTopBannerAdExposure = useCallback(
    (jumpContent?: BannerData['jump_content'], pitsIndex = 0) => {
      try {
        trackEvent(
          buildCampaignTopBannerAdExposureEvent(commonCampaignProperties, jumpContent, pitsIndex),
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Campaign page top banner exposure tracking error:', error);
      }
    },
    [commonCampaignProperties, trackEvent],
  );

  const trackStylePopupExposure = useCallback(
    (popupType: string, popupContent: string) => {
      try {
        trackEvent(
          {
            eventName: 'StylePopUp_Exp',
            properties: {
              page_name: commonCampaignProperties.page_name,
              url_path: typeof window !== 'undefined' ? window.location.href : '',
              popup_type: popupType,
              popup_content: popupContent,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Style popup exposure tracking error:', error);
      }
    },
    [commonCampaignProperties.page_name, trackEvent],
  );

  const trackStylePopupClick = useCallback(
    (popupType: string, popupContent: string, btnName: string) => {
      try {
        trackEvent(
          {
            eventName: 'StylePopUp_Click',
            properties: {
              page_name: commonCampaignProperties.page_name,
              url_path: typeof window !== 'undefined' ? window.location.href : '',
              popup_type: popupType,
              popup_content: popupContent,
              btn_name: btnName,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Style popup click tracking error:', error);
      }
    },
    [commonCampaignProperties.page_name, trackEvent],
  );
  const trackTopBannerAdClick = useCallback(
    (jumpContent?: BannerData['jump_content'], pitsIndex = 0) => {
      try {
        trackEvent(
          buildCampaignTopBannerAdClickEvent(commonCampaignProperties, jumpContent, pitsIndex),
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Campaign page top banner click tracking error:', error);
      }
    },
    [commonCampaignProperties, trackEvent],
  );

  const trackCouponMktExposure = useCallback(
    (
      couponInfo: { id?: number | string | null; name?: string | null },
      index: number,
      moduleName = '优惠券模块',
    ) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageCouponMkt_Exp',
            properties: buildCampaignCouponMktExposureProperties(commonCampaignProperties, {
              moduleName,
              couponIndex: index,
              couponId: couponInfo.id,
              couponName: couponInfo.name,
            }),
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Campaign page coupon exposure tracking error:', error);
      }
    },
    [commonCampaignProperties, trackEvent],
  );

  const trackCouponMktClick = useCallback(
    (
      couponInfo: { id?: number | string | null; name?: string | null },
      index: number,
      moduleName: string,
      buttonName: string,
    ) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageCouponMkt_Click',
            properties: buildCampaignCouponMktClickProperties(commonCampaignProperties, {
              moduleName,
              couponIndex: index,
              couponId: couponInfo.id,
              couponName: couponInfo.name,
              buttonName,
            }),
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Campaign page coupon click tracking error:', error);
      }
    },
    [commonCampaignProperties, trackEvent],
  );

  const trackButtonClick = useCallback(
    (moduleName: string, buttonName: string) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageBtn_Click',
            properties: buildCampaignButtonClickProperties(commonCampaignProperties, {
              moduleName,
              buttonName,
            }),
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Campaign page button click tracking error:', error);
      }
    },
    [commonCampaignProperties, trackEvent],
  );

  const trackCollectButtonClick = useCallback(
    (item: any, index: number, moduleName: string, collectType: string) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        trackEvent(
          {
            eventName: 'CampaignPageCollectBtn_Click',
            properties: buildCampaignCollectButtonClickProperties(commonCampaignProperties, {
              moduleName,
              collectType,
              pitsIndex: index,
              productProperties: buildCampaignProductTrackingProperties(item, {
                productPrice: parseFloat(nowPriceInfo.price || '0'),
                currentProductPrice: parseFloat(nowPriceInfo.sell_price || '0'),
                baseCurrentProductPrice: parseFloat(basePriceInfo.sell_price || '0'),
                dollarExchangeRate: parseFloat(currency?.exchange_rate || '0'),
                currencyName: currency?.name || '',
              }),
            }),
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Campaign page collect button click tracking error:', error);
      }
    },
    [commonCampaignProperties, currency, trackEvent, userInfo],
  );

  useEffect(() => {
    const updateScrollState = () => {
      try {
        lastScrollState.current = {
          viewAdvance: getCurrentScrollPercentage(),
          viewArea: getViewArea(),
        };
      } catch (error) {
        logger.error('Error updating campaign page scroll state:', error);
      }
    };

    updateScrollState();

    window.addEventListener('scroll', updateScrollState, { passive: true });

    return () => {
      window.removeEventListener('scroll', updateScrollState);
    };
  }, []);

  return {
    trackPageView,
    trackPageLeave,
    trackNavTabClick,
    trackViewMoreClick,
    trackProductListPitsClick,
    trackProductListPitsExposure,
    trackAdClick,
    trackAdExp,
    trackTopBannerAdExposure,
    trackStylePopupExposure,
    trackStylePopupClick,
    trackTopBannerAdClick,
    trackCouponMktExposure,
    trackCouponMktClick,
    trackButtonClick,
    trackCollectButtonClick,
  };
}
